import { type ComputedRef, type MaybeRef, computed, unref } from 'vue';
import { type UseQueryReturnType, useQuery } from '@tanstack/vue-query';
import type { Site } from '~/interfaces/sites/main';
import { type SiteDetailResult, type SitesListResult, getAllSites, getSiteDetail, getSitesList } from '~/api/sites';

export const sitesKeys = {
  root: () => ['sites'] as const,
  list: (params: string) => ['sites', 'list', params] as const,
  detail: (id: string | number) => ['sites', 'detail', String(id)] as const,
  all: () => ['sites', 'all'] as const,
};

export function useSitesListQuery (
  params: MaybeRef<string | null>,
): {
    query: UseQueryReturnType<SitesListResult, Error>;
    sites: ComputedRef<Site[]>;
    totalRecords: ComputedRef<number>;
  } {
  const queryKey = computed(() =>
    sitesKeys.list(String(unref(params) ?? '')),
  );

  const query = useQuery<SitesListResult>({
    queryKey,
    queryFn: () => getSitesList(unref(params)),
  });

  const sites = computed<Site[]>(() => query?.data?.value?.items ?? []);
  const totalRecords = computed<number>(() => query?.data?.value?.totalItems ?? 0);

  return {
    query,
    sites,
    totalRecords,
  };
}

export function useSitesAllQuery (): {
  query: UseQueryReturnType<Site[], Error>;
  sites: ComputedRef<Site[]>;
} {
  const query = useQuery<Site[]>({
    queryKey: sitesKeys.all(),
    queryFn: () => getAllSites(),
  });

  const sites = computed<Site[]>(() => query?.data?.value ?? []);

  return {
    query,
    sites,
  };
}

export function useSiteDetailQuery (
  siteId: MaybeRef<string | number | null | undefined>,
): {
    query: UseQueryReturnType<SiteDetailResult, Error>;
    site: ComputedRef<Site | null>;
    logoPath: ComputedRef<string | null>;
    logoExtension: ComputedRef<string | null>;
  } {
  const enabled = computed(() => Boolean(unref(siteId)));

  const queryKey = computed(() =>
    sitesKeys.detail(String(unref(siteId) ?? '')),
  );

  const query = useQuery<SiteDetailResult>({
    queryKey,
    queryFn: () => {
      const id = unref(siteId);
      if (!id) { throw new Error('Site ID is required'); }
      return getSiteDetail(String(id));
    },
    enabled,
  });

  const site = computed<Site | null>(() => query?.data?.value?.item ?? null);
  const logoPath = computed<string | null>(() => query?.data?.value?.logoPath ?? null);
  const logoExtension = computed<string | null>(() => query?.data?.value?.logoExtension ?? null);

  return {
    query,
    site,
    logoPath,
    logoExtension,
  };
}
